import { isAddressInSwap } from '@ui/features/OneClickTrading/constants/allowlist';
import { useDataChainId } from '@ui/hooks/datachain';
import { useIsNeedTokenApproval } from '@ui/hooks/useErc20Read';
import { useRequireAllowance } from '@ui/hooks/useRequireAllowance';
import { Button } from '@ui/lib/park/ui/button';
import { getContractAddress } from '@ui/services/contract/constants';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'next-i18next';
import { useContext } from 'react';
import { Address } from 'viem';
import { useAccount } from 'wagmi';

import { ExchangeContext } from './Context';

export const ExecuteButton = ({ label, disabled }: { label?: string; disabled?: boolean }) => {
  const { onSwap, isSwapPending, canSwap, isQuoteLoading, isInsufficientBalance, inputToken, inputAmount } = useContext(ExchangeContext);
  const { t } = useTranslation();
  const chainId = useDataChainId();
  const { address } = useAccount();

  const isInSwap = isAddressInSwap(address);

  const spender = getContractAddress('LeverUp', chainId);

  const { writeContractAsync: approveAndSwap, isPending: isApprovePending } =
    useRequireAllowance({
      tokenAddress: inputToken?.address || undefined,
      requiredAmount: inputAmount
        ? BigNumber(inputAmount)
          .shiftedBy(inputToken?.decimals || 18)
        : BigNumber(0),
      spender: spender,
      action: onSwap,
    });

  const isLoading = isQuoteLoading || isSwapPending || isApprovePending;

  return <ExecuteButtonContent
    isLoading={isLoading}
    isQuoteLoading={isQuoteLoading}
    canSwap={canSwap}
    onSwap={approveAndSwap}
    isInsufficientBalance={isInsufficientBalance}
    inputToken={inputToken}
    inputAmount={inputAmount}
    spender={spender}
    isInSwap={isInSwap}
    isSwapPending={isSwapPending}
    isApprovePending={isApprovePending}
    customLabel={label}
    disabled={disabled}
  />;
};

const ExecuteButtonContent = ({
  isLoading,
  isQuoteLoading,
  canSwap,
  onSwap,
  isInsufficientBalance,
  inputToken,
  inputAmount,
  spender,
  isInSwap,
  isSwapPending,
  isApprovePending,
  customLabel,
  disabled
}: any) => {
  const { t } = useTranslation();

  const { data: isRequireAllowance } = useIsNeedTokenApproval({
    tokenAddress: inputToken?.address || undefined,
    requiredAmount: inputAmount
      ? BigNumber(inputAmount)
        .shiftedBy(inputToken?.decimals || 18)
      : BigNumber(0),
    spender,
  });

  let label = customLabel || t('common.Swap');
  if (isInsufficientBalance) {
    label = t('common.Insufficient Balance');
  } else if (isLoading) {
    if (isQuoteLoading) label = t('common.Fetching Quote...');
    else if (isApprovePending) label = t('common.Approving...');
    else if (isSwapPending) label = t('common.Swapping...');
    else label = t('common.Processing...');
  } else if (isRequireAllowance && inputToken && !inputToken.isNative) {
    label = t('common.Approve');
  }

  // If native token, never need approval
  if (inputToken?.isNative) {
    if (isInsufficientBalance) label = t('common.Insufficient Balance');
    else if (!isLoading) label = customLabel || t('common.Swap');
  }

  if (!isInSwap) {
    label = t('common.Coming Soon');
  }

  return (
    <Button
      width="full"
      mt={1}
      disabled={!canSwap || isLoading || !isInSwap || disabled}
      loading={isLoading}
      onClick={onSwap}
    >
      {label}
    </Button>
  );
}
